import Form, { type FormInstance } from 'antd/es/form';
import Typography from 'antd/es/typography';
import React from 'react';
import { useTheme } from 'styled-components';

import type { AgentFormData } from '../../AgentModals/CreateAgentModal';
import { AGENT_TYPE_PRESETS } from '../presets';
import { SelectorContainer, TypeGrid, TypeCard, IconWrapper, CardTitle, CardDescription } from './AgentTypeSelector.styles';

const { Title, Paragraph } = Typography;

interface AgentTypeSelectorProps {
  form: FormInstance<AgentFormData>;
  onTypeSelect?: (type: keyof typeof AGENT_TYPE_PRESETS | 'custom') => void;
}

export const AgentTypeSelector: React.FC<AgentTypeSelectorProps> = ({
  form,
  onTypeSelect,
}) => {
  const selectedType = Form.useWatch('agentType', form);
  const theme = useTheme();
  const handleTypeSelect = (
    type: keyof typeof AGENT_TYPE_PRESETS | 'custom',
  ) => {
    form.setFieldValue('agentType', type);
    onTypeSelect?.(type);
  };

  const agentTypes = [
    ...Object.entries(AGENT_TYPE_PRESETS).map(([key, preset]) => ({
      key,
      ...preset,
    })),
    {
      key: 'custom',
      name: 'Custom Agent',
      icon: '🛠️',
      description: 'Build from scratch',
    },
  ];

  return (
    <SelectorContainer>
      <Title level={4} style={{ marginBottom: `${theme.marginXS}px` }}>
        What type of agent do you want to create?
      </Title>
      <Paragraph type="secondary">
        Choose a preset to get started quickly, or build a custom agent from
        scratch.
      </Paragraph>

      <Form.Item
        name="agentType"
        rules={[{ required: true, message: 'Please select an agent type' }]}
      >
        <TypeGrid>
          {agentTypes.map((type) => (
            <TypeCard
              key={type.key}
              $selected={selectedType === type.key}
              onClick={() =>
                handleTypeSelect(
                  type.key as keyof typeof AGENT_TYPE_PRESETS | 'custom',
                )
              }
            >
              <IconWrapper>{type.icon}</IconWrapper>
              <CardTitle>{type.name}</CardTitle>
              <CardDescription>{type.description}</CardDescription>
            </TypeCard>
          ))}
        </TypeGrid>
      </Form.Item>
    </SelectorContainer>
  );
};
